import React, { useMemo, useState } from "react"
import { Breadcrumb, Button, Dropdown, Menu, Drawer, Badge } from "antd"
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FilterOutlined,
  CloseOutlined,
} from "@ant-design/icons"
import { Link, useNavigate } from "react-router-dom"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import "./TitleBox.scss"
import dayjs from "dayjs"
import AssetPath from "../../AssetPath/AssetPath"
import apiClient from "../../Utils/apiClient"

const TitleBox = ({
  title,
  breadcrumbItems = [],
  children,
  NoBack,
  onClick,
  exportable = false,
  exportOptions,
  enableFilter = false,
  filtercontent = null,
  onFilter,
  onClear,
}) => {
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const getActiveFilterCount = (params = {}) => {
    return Object.entries(params).filter(([key, val]) => {
      if (["page", "pageSize"].includes(key)) return false
      if (val === null || val === undefined || val === "") return false
      if (Array.isArray(val) && val.length === 0) return false
      if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        Object.keys(val).length === 0
      ) {
        return false
      }
      return true
    }).length
  }
  const activeFilterCount = useMemo(() => {
    return getActiveFilterCount(exportOptions?.params || {})
  }, [exportOptions?.params])



  const fetchExportData = async () => {
    try {
      const params = {
        ...exportOptions.params,
        page: exportOptions.params?.page ?? 1,
        pageSize: exportOptions.params?.pageSize ?? "99999",
      }

      const options = exportOptions.options || {}
      const res = await apiClient(
        exportOptions?.endpoint,
        exportOptions?.method || "GET",
        params,
        options
      )
      const rawData = res?.data || res.result || []
      console.log("res ======>", res)

      if (exportOptions?.onExportData) {
        exportOptions.onExportData(rawData)
      }

      return exportOptions.format ? exportOptions.format(rawData) : rawData
    } catch (err) {
      console.error("Error fetching export data", err)
      return []
    }
  }

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new()
    let exportData = []
    const ignoreKeys = exportOptions?.excelWithout || []
    if (exportOptions?.data) {
      exportData = exportOptions.data.map((row) => {
        const exportRow = {}

        exportOptions.header.forEach((header, i) => {
          const key = Object.keys(row)[i] || header

          if (ignoreKeys.includes(key)) return

          let value = row[header] ?? row[key] ?? Object.values(row)[i] ?? ""

          if (Array.isArray(value)) {
            if (value.every((v) => typeof v === "object" && v?.hostname)) {
              value = value.map((v) => v.hostname).join(", ")
            } else {
              value = value.join(", ")
            }
          } else if (typeof value === "object" && value !== null) {
            value = JSON.stringify(value)
          }

          exportRow[header] = value
        })

        return exportRow
      })
    }

    const ws = XLSX.utils.json_to_sheet(exportData)

    const colWidths = []
    exportData.forEach((row) => {
      Object.values(row).forEach((value, index) => {
        const length = value ? value.toString().length : 10
        if (!colWidths[index] || colWidths[index] < length) {
          colWidths[index] = Math.min(length + 2, 50)
        }
      })
    })
    ws["!cols"] = colWidths.map((w) => ({ width: w }))

    XLSX.utils.book_append_sheet(wb, ws, "Data")

    const filename = `${
      exportOptions?.fileName || title || exportOptions?.btnTitle || "export"
    }-${new Date().toISOString().split("T")[0]}.xlsx`

    XLSX.writeFile(wb, filename)
  }

  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    })

    let headers, rows
    const ignoreKeys = exportOptions?.pdfWithout || []
    if (exportOptions?.data) {
      headers = exportOptions.header.filter(
        (h, index) =>
          !ignoreKeys.includes(Object.keys(exportOptions.data[0])[index])
      )

      rows = exportOptions.data.map((row) => {
        return Object.entries(row)
          .filter(([key]) => !ignoreKeys.includes(key))
          .map(([_, value]) => {
            if (value === null || value === undefined || value === "") {
              return "-"
            }
            return value
          })
      })
    } else {
      headers = []
      rows = []
    }

    const pdfTitle =
      exportOptions?.fileName || title || exportOptions?.btnTitle || "Export"

    const img = new Image()
    img.src = AssetPath.brand.appIcon
    img.crossOrigin = "Anonymous"

    img.onload = function () {
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")
      ctx.drawImage(img, 0, 0)
      const base64 = canvas.toDataURL("image/png")

      const pageWidth = doc.internal.pageSize.getWidth()

      const logoWidth = 15
      const logoHeight = 12
      const logoY = 7

      doc.setFontSize(14)
      const textWidth =
        (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor

      const gap = 3
      const totalWidth = logoWidth + gap + textWidth

      const startX = (pageWidth - totalWidth) / 2

      doc.addImage(base64, "PNG", startX, logoY, logoWidth, logoHeight)
      doc.text(pdfTitle, startX + logoWidth + gap, logoY + 9)

      autoTable(doc, {
        head: [headers],
        body: rows,
        styles: {
          fontSize: 6,
          cellWidth: "wrap",
          overflow: "linebreak",
          cellPadding: 1,
        },
        headStyles: {
          fillColor: [37, 99, 235],
          fontSize: 7,
          fontStyle: "bold",
        },
        margin: { top: 25 },
        startY: 25,

        didDrawPage: function () {
          const pw = doc.internal.pageSize.getWidth()
          const ph = doc.internal.pageSize.getHeight()

          const x = pw / 2 - 85
          const y = ph / 2 - 60

          doc.setGState(new doc.GState({ opacity: 0.12 }))
          doc.addImage(base64, "PNG", x, y, 170, 120)
          doc.setGState(new doc.GState({ opacity: 1 }))
        },
      })

      const pageCount = doc.internal.getNumberOfPages()
      doc.setPage(pageCount)
      doc.setFontSize(8)
      doc.setTextColor(100)
      const footerText =
        "© 2025 DocumentIT LLC. All rights reserved.\n" +
        '"Device Manager" is a product of DocumentIT LLC. Unauthorized copying, distribution, modification, or use of this software or its components is strictly prohibited.'
      const footerY = doc.internal.pageSize.getHeight() - 10

      doc.text(footerText, pageWidth / 2, footerY, { align: "center" })

      const filename = `${pdfTitle}-${dayjs().format("DD-MM-YYYY")}.pdf`
      doc.save(filename)
    }
  }

    const handleBackClick = () => {
    if (onClick) {
      onClick()
    } else {
      navigate(-1)
    }
  }

  const menu = (
    <Menu>
      <Menu.Item key="excel" onClick={exportToExcel}>
        Export to Excel
      </Menu.Item>
      <Menu.Item key="pdf" onClick={exportToPDF}>
        Export to PDF
      </Menu.Item>
    </Menu>
  )

  return (
    <section className="page-header">
      <div className="page-header__lead">
        <div className="page-header__title-row">
          {!NoBack && (
            <Button
              onClick={handleBackClick}
              shape="circle"
              className="page-header__back"
            >
              <ArrowLeftOutlined />
            </Button>
          )}

          <h1 className="page-header__title">{title}</h1>
        </div>

        {breadcrumbItems.length > 0 && (
          <div className="page-header__crumb">
            <Breadcrumb>
              {breadcrumbItems.length > 1 && (
                <Breadcrumb.Item>
                  <Link to="/">Home</Link>
                </Breadcrumb.Item>
              )}
              {breadcrumbItems.map((item, index) => (
                <Breadcrumb.Item key={index}>
                  {item.href ? (
                    <Link to={item.href}>{item.title}</Link>
                  ) : (
                    <span>{item.title}</span>
                  )}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </div>
        )}
      </div>

      <div className="page-header__actions">
        {children}

        {enableFilter && (
          <>
            <Badge
              count={activeFilterCount}
              offset={[-2, 8]}
              className="me-1"
            >
              <Button
                icon={<FilterOutlined />}
                onClick={() => setDrawerOpen(true)}
                shape="circle"
              />
            </Badge>

            <Drawer
              className="filter-drawer"
              title={
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span>Apply Filter</span>
                  <CloseOutlined
                    onClick={() => setDrawerOpen(false)}
                    className="rounded-circle p-1"
                    style={{
                      color: "rgba(0,0,0,0.6)",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  />
                </div>
              }
              mask={false}
              placement="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              closable={false}
              width={300}
            >
              <div className="d-flex flex-column justify-content-between h-100">
                <>
                  <div className="p-3 pb-0">{filtercontent}</div>
                  {filtercontent && (
                    <div className="p-3 d-flex gap-2">
                      <Button
                        size="small"
                        className="btn-form-filter btn-form-filter--clear rounded-pill"
                        onClick={() => {
                          onFilter()
                          setDrawerOpen(false)
                        }}
                        type="primary"
                      >
                        Apply Filter
                      </Button>
                      <Button
                        size="small"
                        className="btn-form-filter rounded-pill"
                        onClick={() => {
                          onClear()
                          setDrawerOpen(false)
                        }}
                      >
                        Clear Filter
                      </Button>
                    </div>
                  )}
                </>
              </div>
            </Drawer>
          </>
        )}

        {exportable && (
          <Dropdown
            overlay={menu}
            placement="bottomRight"
            arrow
            trigger={["click"]}
          >
            <Button
              icon={<DownloadOutlined />}
              onClick={() => fetchExportData()}
            >
              {exportOptions?.btnTitle || "Export"}
            </Button>
          </Dropdown>
        )}
      </div>
    </section>
  )
}

export default TitleBox
